import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import clsx from 'clsx';
import useFileUpload from 'hooks/fileUpload';
import FPIcon from 'UI/components/atoms/FPIcon';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import TextBox from 'UI/components/atoms/TextBox';
import { CloseIcon, colors, SvgUploadButton } from 'UI/res';
import { nestTernary } from 'UI/utils';

import { useStyles } from './styles';

/** @typedef {import('types/app').AttachmentCategory} AttachmentCategory */
/**
 * @param {Object} props
 * @param {string[]} props.acceptFiles
 * @param {AttachmentCategory} props.category
 * @param {string} props.className
 * @param {string} props.defaultEntityType
 * @param {boolean} [props.disabled]
 * @param {string} [props.deleteEndpoint]
 * @param {string} props.endpoint
 * @param {string} [props.errorText]
 * @param {FileExplorerSection[]} props.fileExplorerSections
 * @param {*} [props.initialValue]
 * @param {string} props.name
 * @param {Function} props.onChange
 * @param {Function} [props.onUploadStarted]
 * @param {boolean} [props.softDeleteForExistingItems]
 */
const FileField = ({
  acceptFiles,
  category,
  className,
  defaultEntityType,
  disabled,
  deleteEndpoint,
  endpoint,
  errorText,
  initialValue,
  name: fieldName,
  onChange,
  onUploadStarted,
  softDeleteForExistingItems,
  fileExplorerSections
}) => {
  const classes = useStyles();

  const {
    canUseFileExplorer,
    file,
    fileName,
    fileRef,
    handleClick,
    handleFileChange,
    handleInputClick,
    isUploading,
    uploadError
  } = useFileUpload({
    initialValue,
    category,
    endpoint,
    deleteEndpoint,
    fieldName,
    onChange,
    onUploadStarted,
    softDeleteForExistingItems,
    defaultEntityType,
    fileExplorerSections
  });

  const { title, required } = category;

  const tooltipText = file
    ? 'Clear'
    : nestTernary(canUseFileExplorer, 'Select a file', 'Upload a file');

  const iconColor = !disabled ? colors.oxford : colors.lightgray;
  return (
    <>
      <TextBox
        name={fieldName}
        label={`${title} ${required ? '*' : ''}`}
        placeholder="Choose a file"
        value={fileName || ''}
        error={!!(errorText || uploadError)}
        errorText={errorText || uploadError}
        className={clsx(classes.input, className)}
        onClick={handleInputClick}
        InputProps={{
          readOnly: true,
          endAdornment: !disabled && (
            <FPIconButton
              onClick={handleClick}
              disabled={isUploading || disabled}
              tooltipProps={{ title: tooltipText }}
            >
              {isUploading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                nestTernary(
                  file,
                  <CloseIcon fill={iconColor} />,
                  <FPIcon color={iconColor} size={24} component={SvgUploadButton} />
                )
              )}
            </FPIconButton>
          )
        }}
        outPutValue={disabled}
      />
      <input
        ref={fileRef}
        type="file"
        accept={acceptFiles.join(',')}
        hidden
        onChange={handleFileChange}
      />
    </>
  );
};

FileField.defaultProps = {
  acceptFiles: [
    'image/png',
    'image/jpg',
    'image/jpeg',
    '.pdf',
    '.docx',
    '.doc',
    '.pptx',
    '.ppt',
    '.xlsx',
    '.xls',
    '.csv',
    '.zip'
  ],
  disabled: false,
  deleteEndpoint: null,
  errorText: null,
  initialValue: null,
  onChange: () => {},
  onUploadStarted: undefined,
  softDeleteForExistingItems: false,
  fileExplorerSections: [],
  className: null
};

export default FileField;
